import React from 'react';
import PropTypes from 'prop-types';

import Popover from 'components/popover';
import ReleaseOptionsMenu from './ReleaseOptionsMenu';
import ReleasePlaylistMenu from './ReleasePlaylistMenu';


/**
 * @desc Release options top-level component
 * @memberof components/ReleaseOptions
 *
 * @param  {Object} track              				[Track for which the menu is rendered]
 * @param  {[Playlist]} playlists                   [List of available playlists]
 * @param  {Boolean} isDownloadAllowed              [Show 'Download' option]
 *
 * @param  {Function} onToggle               		[Dropdown toggle handler]
 * @param  {Function} onDownloadClick               [(Optional) 'Download' option click handler]
 * @param  {Function} onPlayNextClick               ['Play next' option click handler]
 * @param  {Function} onPlayLaterClick              ['Play later' option click handler]
 * @param  {Function} onCreatePlaylist         		['Create playlist' option click handler]
 * @param  {Function} onAddTrackToPlaylist          [Playlist to add selection handler]
 *
 * @type {Component}
 */
export default class ReleaseOptions extends React.PureComponent {
	static propTypes = {
		release: PropTypes.object,
		playlists: PropTypes.arrayOf(PropTypes.object),
		isDownloadAllowed: PropTypes.bool,
		onToggle: PropTypes.func,
		onDownloadClick: PropTypes.func,
		onPlayNextClick: PropTypes.func.isRequired,
		onPlayLaterClick: PropTypes.func.isRequired,
		onCreatePlaylist: PropTypes.func.isRequired,
		onAddTrackToPlaylist: PropTypes.func.isRequired
	}
	static defaultProps = {
		isDownloadAllowed: false
	}

	state = {
		showPlaylistMenu: false
	}

	/* Event Handlers */

	handleAddToPlaylistClick = () => this.setState({ showPlaylistMenu: true });

	handleDownloadClick = () => {
		const { release, onDownloadClick } = this.props;
		return onDownloadClick(release);
	}

	handlePlayNextClick = () => {
		const { release, onPlayNextClick } = this.props;
		this.handleToggle();
		return onPlayNextClick(release);
	}

	handlePlayLaterClick = () => {
		const { release, onPlayLaterClick } = this.props;
		this.handleToggle();
		return onPlayLaterClick(release);
	}

	handlePlaylistClick = (playlist) => {
		const { release, onToggle, onAddTrackToPlaylist } = this.props;

		onAddTrackToPlaylist(playlist, release);
		onToggle();
	}

	handleCreatePlaylistClick = (e) => {
        e.stopPropagation();
		const { release, onToggle, onCreatePlaylist } = this.props;

		onCreatePlaylist(release);
		onToggle();
	}

	handleToggle = () => {
		const { onToggle } = this.props;
		if (typeof onToggle === 'function') onToggle();

		this.setState({ showPlaylistMenu: false });
	}

	/* Rendering */

	renderOptionsMenu = (isDownloadAllowed, releaseLink) => (
		<ReleaseOptionsMenu
			releaseLink={releaseLink}
			isDownloadAllowed={isDownloadAllowed}
			onDownloadClick={this.handleDownloadClick}
			onAddToPlaylistClick={this.handleAddToPlaylistClick} />
	);

	renderPlaylistMenu = (playlists) => (
		<ReleasePlaylistMenu
			playlists={playlists}
			onPlaylistClick={this.handlePlaylistClick}
			onPlayNextClick={this.handlePlayNextClick}
			onPlayLaterClick={this.handlePlayLaterClick}
			onCreatePlaylistClick={this.handleCreatePlaylistClick} />
	);

	render() {
		// Destructure to remove extraneous props from ...rest
		const {
			release,
			playlists,
			isDownloadAllowed,
			onToggle,
			onDownloadClick,
			onPlayNextClick,
			onPlayLaterClick,
			...rest
		} = this.props;

		const { showPlaylistMenu } = this.state;

		const releaseLink = '/releases/releaseInfo/' + release.releaseid;

		return (
			<Popover key={showPlaylistMenu} onToggle={this.handleToggle} {...rest}>
				{
					showPlaylistMenu
						? this.renderPlaylistMenu(playlists)
						: this.renderOptionsMenu(isDownloadAllowed, releaseLink)
				}
			</Popover>
		);
	}
}
